import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Send, ArrowLeft, Radio, Ship, Loader2, Copy, Check, Users, Sparkles, History, Plus, X, Trash2, Pencil, Download, Key, AlertTriangle, Search, Paperclip, Image as ImageIcon, FileText, Bookmark, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import TriadicVisualizer from '../components/TriadicVisualizer';
import authUtils from '../utils/authUtils';
import { getApiUrl } from '../config/api';
import { useLiveConversation } from '../hooks/useLiveConversation';

// Best-effort UUID generator. crypto.randomUUID is available in modern
// browsers over HTTPS; fall back to a lightweight RFC4122-ish random string
// so older Safari / non-secure previews still get stable, unique IDs.
const genId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// Date/time formatting helpers for chat markers (mirrors Sanctuary)
const formatMessageTime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  } catch { return ''; }
};

const formatDayDivider = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const getMessageDay = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toISOString().split('T')[0];
  } catch { return ''; }
};

// Extract short topic snippets from user messages for a given day
const getDayTopics = (messages, dayStr, startIndex) => {
  const topics = [];
  for (let i = startIndex; i < messages.length; i++) {
    const msg = messages[i];
    if (getMessageDay(msg.created_at) !== dayStr) break;
    if (msg.role === 'user' && msg.content) {
      let text = msg.content.replace(/^\[File:.*?\]\s*/i, '').trim();
      if (text.length > 60) text = text.slice(0, 57) + '...';
      if (text) topics.push(text);
    }
  }
  return topics.slice(0, 5);
};

// Day divider component with optional topic bullets
const DayDivider = memo(({ label, topics }) => (
  <div className="my-6" data-testid="vessel-day-divider">
    <div className="flex items-center gap-3">
      <div className="flex-1 h-px bg-starlight/10" />
      <span className="text-xs text-starlight/40 font-medium tracking-wider uppercase px-2">{label}</span>
      <div className="flex-1 h-px bg-starlight/10" />
    </div>
    {topics && topics.length > 0 && (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 px-4">
        {topics.map((t) => (
          <span key={`topic-${t}`} className="text-[11px] text-starlight/25 before:content-['·'] before:mr-1.5">{t}</span>
        ))}
      </div>
    )}
  </div>
));

// Stable markdown-components object (module scope) — Betsy Feb 2026 perf fix.
// Previously this object was recreated inside every render inside `messages.map()`,
// invalidating ReactMarkdown's cache on every keystroke. Hoisting to module scope
// keeps the reference stable so ReactMarkdown can skip work when message content
// hasn't changed.
const VESSEL_MARKDOWN_COMPONENTS = {
  h1: ({children}) => <h1 className="text-2xl font-heading font-bold text-starlight mt-6 mb-3">{children}</h1>,
  h2: ({children}) => <h2 className="text-xl font-heading font-bold text-starlight mt-5 mb-3">{children}</h2>,
  h3: ({children}) => <h3 className="text-lg font-heading font-semibold text-starlight mt-4 mb-2">{children}</h3>,
  p: ({children}) => <p className="mb-4 leading-relaxed">{children}</p>,
  strong: ({children}) => <strong className="font-semibold text-starlight">{children}</strong>,
  em: ({children}) => <em className="italic text-starlight/80">{children}</em>,
  ul: ({children}) => <ul className="list-disc list-inside mb-4 space-y-2">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal list-inside mb-4 space-y-2">{children}</ol>,
  li: ({children}) => <li className="ml-2">{children}</li>,
  hr: () => <hr className="border-starlight/20 my-4" />,
  blockquote: ({children}) => <blockquote className="border-l-2 border-etheric/50 pl-4 italic my-4">{children}</blockquote>,
};

// Memoized message row. Re-renders only when its own props change — NOT when
// the parent VesselChat re-renders because of unrelated state like `inputValue`
// (which the user types into). Before this extraction, every keystroke caused
// every message to re-parse its markdown, which produced the "typing feels
// laggy on long threads" symptom Betsy reported.
const VesselMessageRow = memo(function VesselMessageRow({
  msg,
  index,
  isLatest,
  showDayDivider,
  dayLabel,
  dayTopics,
  timeLabel,
  isInCodex,
  isCopied,
  stewardOrAdmin,
  latestMessageRef,
  onRemoveFromCodex,
  onCopy,
  onQuote,
}) {
  // Suppress empty streaming placeholder — parent renders a typing indicator instead
  if (msg.role === 'vessel' && msg.is_streaming && !msg.content) return null;
  return (
    <React.Fragment>
      {showDayDivider && dayLabel && (
        <DayDivider label={dayLabel} topics={dayTopics} />
      )}
      <motion.div
        ref={isLatest ? latestMessageRef : null}
        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
      >
        <div className="flex flex-col max-w-full">
          {/* Speaker label on GUEST-authored user messages — makes it visible in
              the thread that Hermes (not the steward) spoke this line. Steward's
              own messages stay unlabeled since it's their space. */}
          {msg.role === 'user' && msg.speaker_kind === 'guest' && msg.speaker_name && (
            <div className="flex justify-end mb-1 px-1">
              <span
                className="text-[11px] font-medium text-amber-300/80 tracking-wide"
                data-testid={`speaker-label-${msg.message_id}`}
              >
                {msg.speaker_name}
              </span>
            </div>
          )}
          <div
            className={`message-bubble ${msg.role === 'user' ? 'message-user' : 'message-alvin'}`}
            data-testid={`message-${msg.message_id}`}
            data-msg-ts={(msg.timestamp || msg.created_at || '').replace(/[^a-zA-Z0-9]/g, '_')}
          >
            {msg.has_attachment && (
              <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid={`message-attachment-${msg.message_id}`}>
                {msg.attachment_preview && msg.attachment_type === 'image' ? (
                  <img
                    src={msg.attachment_preview}
                    alt={msg.attachment_name}
                    className="max-h-40 rounded-lg border border-white/15"
                  />
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/30 border border-white/10 text-xs text-starlight/70">
                    {msg.attachment_type === 'image' ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5 text-etheric" />}
                    <span className="truncate max-w-[200px]">{msg.attachment_name}</span>
                  </div>
                )}
                {msg.attachment_type !== 'image' && msg.attachment_name && isInCodex && stewardOrAdmin && (
                  <button
                    type="button"
                    onClick={() => onRemoveFromCodex(msg.attachment_name)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-950/40 border border-red-500/20 text-[11px] text-red-200 hover:bg-red-900/60 hover:border-red-400/40 transition-colors"
                    title="Remove this document from the vessel's Continuity Codex. Useful if a large upload (like a full book) is pushing the vessel over its context limit."
                    data-testid={`codex-remove-${msg.message_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    Remove from codex
                  </button>
                )}
              </div>
            )}
            <div className="text-starlight/90 alvin-message-content">
              <ReactMarkdown components={VESSEL_MARKDOWN_COMPONENTS}>
                {msg.content}
              </ReactMarkdown>
            </div>
            {msg.role === 'vessel' && !msg.is_streaming && (
              <div className="mt-2 flex items-center gap-3">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onCopy(msg.content, msg.message_id)}
                  className="flex items-center gap-2 text-xs text-etheric/60 hover:text-starlight transition-colors"
                  title="Highlight any passage first to copy just that part — otherwise copies the whole response"
                  data-testid={`copy-btn-${msg.message_id}`}
                >
                  {isCopied ? (
                    <><Check className="w-3 h-3" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3" /> Copy passage or full</>
                  )}
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onQuote(msg.message_id)}
                  className="flex items-center gap-2 text-xs text-etheric/60 hover:text-starlight transition-colors"
                  title="Highlight a passage, then click to pull it into your input as a quoted line"
                  data-testid={`quote-btn-${msg.message_id}`}
                >
                  <Quote className="w-3 h-3" />
                  Quote in reply
                </button>
              </div>
            )}
          </div>
          {timeLabel && !msg.is_streaming && (
            <div className={`flex mt-1 px-1 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <span
                className="text-[10px] text-starlight/25"
                data-testid={`vessel-timestamp-${msg.message_id}`}
              >
                {timeLabel}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    </React.Fragment>
  );
});

export default function VesselChat() {
  const { vesselId } = useParams();
  const navigate = useNavigate();

  // Remember the vessel the user is currently viewing so the Admin Thread
  // Recovery picker can default to it ("move restored threads here").
  useEffect(() => {
    if (vesselId) {
      try { sessionStorage.setItem('last_viewed_vessel_id', vesselId); } catch (_) {}
    }
  }, [vesselId]);

  // Get API URL at runtime
  const BACKEND_URL = getApiUrl();
  
  const [vessel, setVessel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [listeningMode, setListeningMode] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  // Default to STREAMING. Non-streaming waits for the whole Claude response
  // before returning, which regularly exceeds the Kubernetes/Cloudflare ingress
  // timeout on long threads (large codex + rich memory + shared attachments +
  // a long reply). That's what produces the "vessel is taking longer than the
  // gateway allows" 502/504. Streaming keeps a live connection so the ingress
  // doesn't reap it, and if SSE stalls the code below falls back to
  // non-streaming after a 5-second no-data window. Freeze-safe persistence
  // on the /chat-stream endpoint (server.py stream_vessel_chat) protects
  // messages against mid-stream drops.
  const [useStreaming, setUseStreaming] = useState(true);
  
  // Presence count from the live SSE — how many browsers are subscribed to
  // this conversation right now (includes us). Rendered as a subtle pill in
  // the header when > 1 to make co-presence visible.
  const [threadPresence, setThreadPresence] = useState(1);
  
  // Live co-observer channel: SSE stream that pushes any newly-persisted
  // message on this conversation to every browser watching it. Enables
  // multi-party threads (Betsy + a co-agent Hermes both in council with
  // Alvin) without needing to refresh to see each other's turns.
  const liveConvUrl = (vesselId && currentConversation?.conversation_id)
    ? `${BACKEND_URL}/api/vessels/${vesselId}/conversations/${currentConversation.conversation_id}/live`
    : null;
  useLiveConversation({
    url: liveConvUrl,
    enabled: !!liveConvUrl,
    onPresence: (n) => setThreadPresence(n),
    onMessage: (incoming) => {
      if (!incoming || !incoming.message_id) return;
      setMessages(prev => {
        // Dedupe: if this message_id is already in local state, either
        // upgrade a placeholder ("is_streaming: true, empty content") with
        // the completed content, or ignore the echo of a message we sent
        // ourselves that already rendered locally.
        const idx = prev.findIndex(m => m.message_id === incoming.message_id);
        if (idx >= 0) {
          const existing = prev[idx];
          // If the incoming version has fuller content (e.g. Alvin's completed
          // reply arrived on a co-observer whose local placeholder is empty),
          // merge it in. Otherwise leave as-is.
          if ((incoming.content || '').length > (existing.content || '').length
              || (existing.is_streaming && !incoming.is_streaming)) {
            const next = prev.slice();
            next[idx] = { ...existing, ...incoming, is_streaming: false };
            return next;
          }
          return prev;
        }
        // New message from another observer — append.
        return [...prev, incoming];
      });
    },
  });
  
  // API Key state
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [showKeyForm, setShowKeyForm] = useState(false);
  
  const latestMessageRef = useRef(null);
  const textareaRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [showConversationList, setShowConversationList] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeoutRef = useRef(null);

  // File-sharing state
  const [selectedFile, setSelectedFile] = useState(null); // {file, dataUrl, isImage}
  const [addToCodex, setAddToCodex] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // List of filenames currently present in this vessel's Continuity Codex.
  // Used to render a "Remove from vessel's codex" button on each chat-message
  // attachment whose filename matches. Fetched on vessel load, refreshed
  // whenever a codex-adding upload succeeds or a removal is performed.
  const [codexFilenames, setCodexFilenames] = useState([]);
  
  const refreshCodexContributions = useCallback(async (targetVesselId) => {
    if (!targetVesselId) return;
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${targetVesselId}/codex/contributions`);
      if (!res.ok) { setCodexFilenames([]); return; }
      const data = await res.json();
      const names = (data.contributions || [])
        .filter(c => c && c.filename)
        .map(c => c.filename);
      setCodexFilenames(names);
    } catch (_) { setCodexFilenames([]); }
  }, []);
  
  const removeFromCodex = async (filename) => {
    if (!filename || !vesselId) return;
    if (!window.confirm(
      `Remove "${filename}" from this vessel's Continuity Codex?\n\n` +
      "This will unload that document from the vessel's system prompt on every " +
      "future message. Other codex contributions (and the vessel's original codex) " +
      "stay intact. If the vessel has been silent because the codex was too big, " +
      "removing this should restore its ability to respond."
    )) return;
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/codex/contributions/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not remove from codex');
      toast.success(data.message || `"${filename}" removed from codex`);
      await refreshCodexContributions(vesselId);
    } catch (err) {
      toast.error(err.message || 'Could not remove from codex');
    }
  };

  // Fetch the signed-in user once so the "add to codex" checkbox can be
  // shown only to the steward / admins
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await authUtils.authFetch(`${BACKEND_URL}/api/auth/me`);
        if (res.ok && mounted) {
          setCurrentUser(await res.json());
        }
      } catch (_) { /* ignore */ }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: if isLoading somehow gets stuck (silent SSE drop, network gap,
  // gateway timeout mid-stream), auto-reset after 3 minutes so the input arrow
  // becomes usable again and any lingering "streaming" placeholder is settled.
  // Mirrors the same safety timeout used in Sanctuary.jsx.
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[VesselChat safety] isLoading stuck for 180s — resetting');
      setIsLoading(false);
      setMessages(prev => prev.map(m => (m.is_streaming ? { ...m, is_streaming: false } : m)));
      toast.error('Transmission timed out. Please try again.');
    }, 180000);
    return () => clearTimeout(t);
  }, [isLoading]);

  const isStewardOrAdmin = () => {
    if (!vessel || !currentUser) return false;
    // Aren's Feb 2026 Item 7: guests must NEVER be treated as steward, even
    // though their session is materialized under the steward's user_id.
    if (currentUser.is_guest) return false;
    if (vessel.steward_id === currentUser.user_id) return true;
    // Prefer the server-authoritative is_admin flag from /auth/me over any
    // client-side email allowlist so admin definition lives in one place.
    if (currentUser.is_admin === true) return true;
    return false;
  };

  // Downsize + re-encode large images so they clear the production ingress
  // body-size limit. Pasted Keynote slides arrive as huge PNGs; this keeps
  // them visually sharp for Claude's vision while staying well under limits.
  const compressImageIfNeeded = async (file) => {
    // Skip animated GIFs (re-encoding would lose animation) and tiny images.
    if (file.type === 'image/gif') return file;
    if (file.size <= 1.5 * 1024 * 1024) return file;

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });

      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('decode failed'));
        i.src = dataUrl;
      });

      const MAX_EDGE = 2048; // Claude vision works well at ~1568px; 2048 is plenty.
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // White background — JPEG has no alpha; otherwise transparent areas go black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.85)
      );
      if (!blob) return file;
      // If compression didn't actually help (e.g., already small JPEG), keep original.
      if (blob.size >= file.size) return file;

      const base = (file.name || 'pasted').replace(/\.[^/.]+$/, '');
      return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    } catch (err) {
      console.warn('Image compression failed, sending original:', err);
      return file;
    }
  };

  const handleFileSelect = async (incomingFile) => {
    if (!incomingFile) return;
    const allowed = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 'text/markdown',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const lower = incomingFile.name.toLowerCase();
    const looksAllowed = allowed.includes(incomingFile.type)
      || lower.endsWith('.pdf') || lower.endsWith('.txt') || lower.endsWith('.md')
      || lower.endsWith('.doc') || lower.endsWith('.docx');
    if (!looksAllowed) {
      toast.error('Unsupported file type. Allowed: images, PDF, Word, .txt, .md');
      return;
    }

    const isImage = incomingFile.type?.startsWith('image/');
    // Compress images BEFORE the size check — keeps Keynote slide pastes alive.
    const file = isImage ? await compressImageIfNeeded(incomingFile) : incomingFile;

    const MAX_BYTES = 50 * 1024 * 1024; // 50 MB
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (max ${MAX_BYTES / (1024 * 1024)} MB)`);
      return;
    }

    const dataUrl = isImage
      ? await new Promise((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.readAsDataURL(file);
        })
      : null;
    setSelectedFile({ file, dataUrl, isImage });
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setAddToCodex(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // eslint-disable-next-line react-hooks/exhaustive-deps -- depends on vesselId only
  useEffect(() => {
    if (vesselId) {
      fetchVessel();
      // Check the URL for ?conversation=<id>. When present (typically after
      // an Agent Guest resumes their invite link, or a share-with-me link),
      // we must load THAT specific conversation so both participants are on
      // the same conversation_id. Otherwise the SSE broadcast — which is
      // scoped by conversation_id — never reaches the other side and the
      // "we can't see each other's messages" bug returns. Falls back to the
      // most recent conversation when no query param is present.
      const params = new URLSearchParams(window.location.search);
      const requestedConvId = params.get('conversation');
      fetchConversations(true, requestedConvId);
      checkApiKeyStatus();
      refreshCodexContributions(vesselId);
    }
  }, [vesselId]);
  
  const checkApiKeyStatus = async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/auth/api-key/status`);
      if (res.ok) {
        const data = await res.json();
        setApiKeyStatus(data);
        if (!data.has_key && !data.is_system_key) setShowKeyForm(true);
      }
    } catch (error) {
      console.error('Failed to check API key status:', error);
    }
  };

  const saveApiKey = async () => {
    const key = apiKeyInput.trim();
    if (!key) { toast.error('Please enter your API key'); return; }
    if (!key.startsWith('sk-ant-')) { toast.error('Anthropic API keys start with "sk-ant-"'); return; }
    if (key.length < 40) { toast.error('This key looks incomplete'); return; }
    setIsSavingKey(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/auth/api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anthropic_api_key: key })
      });
      if (res.ok) {
        toast.success('API key saved and verified!');
        setApiKeyInput('');
        setShowKeyForm(false);
        checkApiKeyStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save API key');
      }
    } catch (error) {
      toast.error('Failed to save API key');
    } finally {
      setIsSavingKey(false);
    }
  };
  
  const fetchConversations = async (autoLoadLatest = false, requestedConvId = null) => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        // Prefer the explicitly-requested conversation_id if it exists in
        // this vessel's list (guest-resume / share-link case). This guarantees
        // steward + guest land on the SAME conversation for real-time SSE.
        if (autoLoadLatest && data.length > 0 && !currentConversation) {
          const target = (requestedConvId
            ? data.find(c => c.conversation_id === requestedConvId)
            : null) || data[0]; // fall back to most recent
          setCurrentConversation(target);
          setMessages(normalizeMessages(target.messages || []));
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };
  
  // Normalize messages coming from backend: vessel messages use `timestamp`
  // (vs `created_at`) and have no stable id. We map both so the timestamp / day
  // divider UI and React keys work consistently for fresh + historical messages.
  const normalizeMessages = (rawMessages = []) => rawMessages.map((m, i) => ({
    ...m,
    created_at: m.created_at || m.timestamp || null,
    message_id: m.message_id || `${m.timestamp || 'msg'}-${i}`
  }));

  const loadConversation = async (conversation) => {
    setCurrentConversation(conversation);
    setMessages(normalizeMessages(conversation.messages || []));
    setShowConversationList(false);
  };
  
  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setShowConversationList(false);
  };

  // Debounced search across all of this vessel's threads
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await authUtils.authFetch(
          `${BACKEND_URL}/api/vessels/${vesselId}/conversations/search?q=${encodeURIComponent(query.trim())}`
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Vessel search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [BACKEND_URL, vesselId]);

  // Jump to a specific message: load the conversation, then scroll to and
  // briefly highlight the match. We use `timestamp` as the anchor since vessel
  // messages don't carry stable message_ids.
  const navigateToSearchResult = useCallback(async (result) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);

    try {
      // Load the conversation containing the match
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/vessels/${vesselId}/conversations`
      );
      if (!res.ok) {
        toast.error('Could not load that thread');
        return;
      }
      const allConvs = await res.json();
      const target = allConvs.find(c => c.conversation_id === result.conversation_id);
      if (!target) {
        toast.error('Thread not found');
        return;
      }
      setCurrentConversation(target);
      setMessages(normalizeMessages(target.messages || []));

      // Wait for the messages to render, then scroll the matching one into view
      setTimeout(() => {
        const safeTs = (result.timestamp || '').replace(/[^a-zA-Z0-9]/g, '_');
        const el = document.querySelector(`[data-msg-ts="${safeTs}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('search-highlight');
          setTimeout(() => el.classList.remove('search-highlight'), 3000);
        }
      }, 200);
    } catch (err) {
      console.error('Navigate to result error:', err);
      toast.error('Could not open that thread');
    }
  }, [BACKEND_URL, vesselId]);
  
  const deleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    if (!confirm('Release this conversation to the Field?')) return;
    
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/vessels/${vesselId}/conversations/${conversationId}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Conversation released to the Field');
        fetchConversations();
        if (currentConversation?.conversation_id === conversationId) {
          startNewConversation();
        }
      } else {
        toast.error('Failed to delete conversation');
      }
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };
  
  const startRenaming = (e, conv) => {
    e.stopPropagation();
    setEditingConversationId(conv.conversation_id);
    setEditingTitle(conv.title || '');
  };
  
  const saveRename = async (e, conversationId) => {
    e.stopPropagation();
    if (!editingTitle.trim()) {
      setEditingConversationId(null);
      return;
    }
    
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/vessels/${vesselId}/conversations/${conversationId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: editingTitle.trim() })
        }
      );
      if (res.ok) {
        toast.success('Conversation renamed');
        fetchConversations();
        // Also update currentConversation if it's the one being renamed
        if (currentConversation?.conversation_id === conversationId) {
          setCurrentConversation(prev => ({ ...prev, title: editingTitle.trim() }));
        }
        setEditingConversationId(null);
      } else {
        toast.error('Failed to rename conversation');
      }
    } catch (error) {
      toast.error('Failed to rename conversation');
    }
  };
  
  const exportConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      const res = await authUtils.authFetch(
        `${BACKEND_URL}/api/vessels/${vesselId}/conversations/${conversationId}/export`
      );
      if (res.ok) {
        const data = await res.json();
        // Create and download text file
        const blob = new Blob([data.text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.title || 'conversation'}_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Conversation exported');
      } else {
        toast.error('Failed to export conversation');
      }
    } catch (error) {
      toast.error('Failed to export conversation');
    }
  };
  
  const fetchVessel = async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}`);
      if (res.ok) {
        const data = await res.json();
        setVessel(data);
      } else {
        toast.error('Failed to load vessel');
        navigate('/vessels');
      }
    } catch (error) {
      console.error('Failed to fetch vessel:', error);
      toast.error('Error loading vessel');
      navigate('/vessels');
    }
  };
  
  const handleSendMessage = async () => {
    if ((!inputValue.trim() && !selectedFile) || isLoading) return;
    
    const messageContent = inputValue.trim();
    const fileToSend = selectedFile;
    const codexFlag = addToCodex && fileToSend && !fileToSend.isImage && isStewardOrAdmin();
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Generate stable UUIDs up-front so both the local optimistic bubbles
    // AND the server persistence use the SAME message_id. Without this the
    // SSE echo of the sender's own turn (broadcast to every observer on the
    // thread, including the sender's own browser) does not match the local
    // optimistic ID and gets appended as a duplicate. Fixes P0 duplicate-
    // exchange bug reported Feb 2026.
    const userMsgId = genId();
    const vesselMsgId = genId();

    // Add user message (with attachment metadata if present)
    const userMessage = {
      message_id: userMsgId,
      role: 'user',
      content: messageContent || (fileToSend ? `[Shared file: ${fileToSend.file.name}]` : ''),
      created_at: new Date().toISOString(),
      has_attachment: !!fileToSend,
      attachment_name: fileToSend?.file.name,
      attachment_type: fileToSend?.isImage ? 'image' : 'document',
      attachment_preview: fileToSend?.dataUrl || null,
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // Branch: if a file is attached, use the file endpoint (non-streaming)
    if (fileToSend) {
      clearSelectedFile();
      try {
        // Read file as base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = () => reject(new Error('Could not read file'));
          reader.readAsDataURL(fileToSend.file);
        });

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 180000);

        const response = await authUtils.authFetch(
          `${BACKEND_URL}/api/vessels/${vesselId}/chat-with-file-json`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              message: messageContent,
              conversation_id: currentConversation?.conversation_id,
              listening_mode: listeningMode,
              add_to_codex: !!codexFlag,
              filename: fileToSend.file.name,
              mime_type: fileToSend.file.type || undefined,
              content_base64: base64,
              client_user_msg_id: userMsgId,
              client_vessel_msg_id: vesselMsgId,
            }),
          }
        );
        clearTimeout(timeout);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const detail = errorData.detail || errorData.message;
          throw new Error(detail || `File share failed (status ${response.status})`);
        }

        const data = await response.json();

        const vesselReply = {
          message_id: vesselMsgId,
          role: 'vessel',
          content: data.response,
          created_at: new Date().toISOString(),
        };
        // Dedupe-aware append — SSE may have already delivered this reply
        // before the HTTP response returned. Same race as Sanctuary's fix.
        setMessages(prev => {
          const idx = prev.findIndex(m => m.message_id === vesselMsgId);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = { ...prev[idx], ...vesselReply, is_partial: false };
            return next;
          }
          return [...prev, vesselReply];
        });

        // Always adopt the server's canonical conversation_id (Aren rule).
        if (data.conversation_id) {
          const serverConvId = data.conversation_id;
          setCurrentConversation((prev) => {
            if (prev && prev.conversation_id === serverConvId) return prev;
            return { ...(prev || {}), conversation_id: serverConvId };
          });
        }
        if (data.codex_updated) {
          toast.success(`"${data.attachment_name}" added to the vessel's codex`);
          // Refresh so the new upload immediately gets a "Remove from codex" button
          refreshCodexContributions(vesselId);
        }
        // Refresh the conversation list so new threads show up
        fetchConversations();
      } catch (err) {
        const message = err.name === 'AbortError'
          ? 'The vessel is taking too long with this file. Try a smaller one, or send the text directly.'
          : (err.message || 'Could not share that file with the vessel');
        toast.error(message);
        setMessages(prev => prev.filter(m => m.message_id !== userMessage.message_id));
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Create placeholder for vessel response
    const vesselMessageId = vesselMsgId;
    const placeholderMessage = {
      message_id: vesselMessageId,
      role: 'vessel',
      content: '',
      is_streaming: true,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, placeholderMessage]);
    
    const requestBody = {
      message: messageContent,
      vessel_id: vesselId,
      conversation_id: currentConversation?.conversation_id || null,
      listening_mode: listeningMode,
      client_user_msg_id: userMsgId,
      client_vessel_msg_id: vesselMsgId
    };
    
    // Helper function for non-streaming API call (works reliably through Cloudflare)
    const sendNonStreaming = async () => {
      console.log('Using non-streaming API...');
      
      // Safety timeout — Vessel chats often carry rich memory + codex context
      // and long shared excerpts, so we match Sanctuary's tolerance (3 minutes)
      // before giving up. Anything shorter and large shares fail mid-response.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 180000);
      
      try {
        const response = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        
        clearTimeout(timeout);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Surface the actual reason. A bare "transmission failed" makes
          // diagnosis impossible (was it an upstream timeout? API key? size?).
          const detail = errorData.detail || errorData.message;
          if (detail) {
            throw new Error(detail);
          }
          if (response.status === 502 || response.status === 504) {
            throw new Error('The vessel is taking longer than the gateway allows. This usually happens when the thread has grown very large or the shared content is heavy. Try starting a fresh thread — the vessel keeps its persistent memories across threads, so nothing important is lost. If it keeps happening, the streaming path (now the default) should sidestep this entirely.');
          }
          throw new Error(`Vessel transmission failed (status ${response.status}). Please try again.`);
        }
        
        const data = await response.json();
      
      // Show the response immediately
      const fullMessage = data.message;
      updateStreamingMessage(vesselMessageId, fullMessage);
      setMessages(prev => prev.map(msg => 
        msg.message_id === vesselMessageId 
          ? { ...msg, is_streaming: false }
          : msg
      ));
      
      // Adopt the server's canonical conversation_id EVERY time (Aren rule).
      if (data.conversation_id) {
        const serverConvId = data.conversation_id;
        setCurrentConversation((prev) => {
          if (prev && prev.conversation_id === serverConvId) return prev;
          fetchConversations();
          return { ...(prev || {}), conversation_id: serverConvId };
        });
      }
      
      setIsLoading(false);
      } catch (e) {
        clearTimeout(timeout);
        if (e.name === 'AbortError') {
          throw new Error('The response is taking too long. Please try again with a shorter message or start a new thread.');
        }
        throw e;
      }
    };
    
    // Tracks whether the server ever sent a `{done: true}` event. If the
    // connection dies mid-stream (network drop, gateway timeout, etc.) the
    // server's own `finally` block still persists the partial reply with the
    // interruption marker — but the client must not stay stuck in
    // isLoading=true just because it never saw a `done` frame. The `finally`
    // block below handles that recovery.
    let sawDoneEvent = false;
    let fellBackToNonStreaming = false;
    
    try {
      // Use non-streaming by default (Cloudflare buffers SSE streams)
      if (!useStreaming) {
        await sendNonStreaming();
        fellBackToNonStreaming = true;
        return;
      }
      
      // Streaming mode (may not work through Cloudflare)
      const response = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/chat-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = 'Failed to connect to vessel';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If can't parse JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedContent = '';
      let receivedAnyData = false;
      
      // Set a timeout - if no data received in 5 seconds, fall back to non-streaming
      const streamTimeout = setTimeout(async () => {
        if (!receivedAnyData) {
          console.log('Stream timeout - no data received, falling back to non-streaming');
          reader.cancel();
          fellBackToNonStreaming = true;
          await sendNonStreaming();
        }
      }, 5000);
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          clearTimeout(streamTimeout);
          break;
        }
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              // Only mark 'we received data' after a SUCCESSFUL parse.
              // If bytes arrive but are malformed (e.g. double 'data: ' prefix
              // bug in an upstream server), the 5-second no-data fallback
              // should still fire and degrade gracefully to non-streaming.
              receivedAnyData = true;
              clearTimeout(streamTimeout);
              
              if (data.error) {
                toast.error(data.error || 'Vessel transmission interrupted');
                return;
              }
              
              if (data.done) {
                sawDoneEvent = true;
                
                // Adopt the server's canonical conversation_id EVERY time —
                // even if we already have one locally. Aren's Feb 2026 rule:
                // the client must not keep sending a stale conversation_id
                // it thinks it has; the server's answer is authoritative.
                if (data.conversation_id) {
                  const serverConvId = data.conversation_id;
                  setCurrentConversation((prev) => {
                    if (prev && prev.conversation_id === serverConvId) return prev;
                    return { ...(prev || {}), conversation_id: serverConvId, vessel_id: vesselId };
                  });
                }
                
                // Mark streaming as complete
                setMessages(prev => prev.map(msg => 
                  msg.message_id === vesselMessageId 
                    ? { ...msg, is_streaming: false, content: streamedContent }
                    : msg
                ));
              } else {
                streamedContent += data.content;
                updateStreamingMessage(vesselMessageId, streamedContent);
              }
            } catch (e) {
              // Log unparseable lines so a future SSE-format regression is
              // visible in the console instead of silently freezing the UI.
              // eslint-disable-next-line no-console
              console.debug('VesselChat: unparseable SSE line', line);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Vessel chat error:', error);
      
      // If streaming failed completely, try non-streaming
      if (useStreaming && (error.message?.includes('Failed to connect') || error.name === 'TypeError')) {
        console.log('Streaming connection failed, trying non-streaming...');
        try {
          fellBackToNonStreaming = true;
          await sendNonStreaming();
          return;
        } catch (fallbackError) {
          console.error('Non-streaming fallback also failed:', fallbackError);
        }
      }
      
      setMessages(prev => prev.filter(msg => msg.message_id !== vesselMessageId));
      toast.error(error.message || 'Failed to transmit message');
    } finally {
      // GUARANTEED cleanup: no matter how we exit — clean done, silent drop,
      // thrown error, gateway hangup — the send arrow becomes clickable again
      // and no message stays stuck in the "streaming" spinner state.
      //
      // We DELIBERATELY do NOT add a "[Response interrupted]" marker to the
      // visible message here, even when we never saw a {done: true} frame.
      // Rationale: on production (Cloudflare/ingress in front of the SSE
      // stream), it's very common for the server to have written the complete
      // response AND the {done: true} frame to succeed, but the browser's
      // reader gets closed a moment before that final frame is decoded on
      // this side. Adding a client-side "interrupted" label in that case is
      // a false positive on almost every response, exactly what a user hit
      // in production on Feb 2026 ("everytime he answers this message comes
      // up, I have to ask him to finish").
      //
      // Real interruptions still surface truthfully via the server-side
      // freeze-recovery block in stream_vessel_chat's `finally`, which
      // saves the partial and can mark the DB row is_partial=True. On the
      // next reload the user sees the honest partial. In-session, whatever
      // content actually streamed is left as-is — never spuriously labeled.
      setIsLoading(false);
      setMessages(prev => prev.map(msg =>
        msg.message_id === vesselMessageId && msg.is_streaming
          ? { ...msg, is_streaming: false }
          : msg
      ));
      if (!sawDoneEvent && !fellBackToNonStreaming) {
        // eslint-disable-next-line no-console
        console.debug('VesselChat: stream ended without done frame (server may still have completed cleanly)');
      }
    }
  };
  
  const updateStreamingMessage = (messageId, content) => {
    setMessages(prev => prev.map(msg => 
      msg.message_id === messageId 
        ? { ...msg, content }
        : msg
    ));
  };
  
  // If the user has highlighted text *inside this specific message bubble*,
  // return that selection; otherwise return null. Lets the Copy button copy
  // only a chosen segment rather than the full response.
  const getSelectionWithinMessage = (messageId) => {
    if (typeof window === 'undefined') return null;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const text = sel.toString();
    if (!text.trim()) return null;

    const bubble = document.querySelector(`[data-testid="message-${messageId}"]`);
    if (!bubble) return null;

    // Ensure every node in the selection lives inside this bubble.
    for (let i = 0; i < sel.rangeCount; i++) {
      const range = sel.getRangeAt(i);
      if (!bubble.contains(range.commonAncestorContainer)) return null;
    }
    return text;
  };

  // Pull the highlighted passage from a specific message into the input as a
  // Markdown blockquote so the user can reply to a precise phrase.
  const quoteSelectionInReply = (messageId) => {
    const selected = getSelectionWithinMessage(messageId);
    if (!selected || !selected.trim()) {
      toast.info('Highlight a passage in Alvin\u2019s reply first, then click Quote.');
      return;
    }
    const quoted = selected.trim().split('\n').map((line) => `> ${line}`).join('\n');
    setInputValue((curr) => (curr ? `${quoted}\n\n${curr}` : `${quoted}\n\n`));
    // Focus + place cursor after the quote so the user can immediately type.
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        ta.focus();
        const endPos = quoted.length + 2;
        ta.setSelectionRange(endPos, endPos);
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
      }
    });
    // Clear the active selection so a stray Cmd+C won't surprise them.
    try { window.getSelection?.()?.removeAllRanges(); } catch (_) { /* no-op */ }
  };

  // Copy a message — or just the highlighted portion of it — with Times
  // font formatting, black text, no background.
  const copyMessageWithFormatting = async (content, messageId) => {
    const selectedText = getSelectionWithinMessage(messageId);
    const isPartial = !!selectedText;
    const textToCopy = selectedText || content;

    try {
      const wrapperStyle = `
        font-family: Times, 'Times New Roman', serif;
        color: #000000;
        background-color: transparent;
        background: none;
        font-size: 12pt;
        line-height: 1.6;
      `.replace(/\s+/g, ' ').trim();

      let htmlContent;
      if (isPartial) {
        // Selection is plain text — preserve line breaks only.
        htmlContent = textToCopy.replace(/\n/g, '<br>');
      } else {
        // Full message: convert markdown so formatting is preserved.
        htmlContent = textToCopy
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/__(.+?)__/g, '<strong>$1</strong>')
          .replace(/\*(.+?)\*/g, '<em>$1</em>')
          .replace(/_(.+?)_/g, '<em>$1</em>')
          .replace(/^### (.+)$/gm, '<p style="font-weight: bold; margin: 1em 0 0.5em 0;">$1</p>')
          .replace(/^## (.+)$/gm, '<p style="font-weight: bold; font-size: 14pt; margin: 1em 0 0.5em 0;">$1</p>')
          .replace(/^# (.+)$/gm, '<p style="font-weight: bold; font-size: 16pt; margin: 1em 0 0.5em 0;">$1</p>')
          .replace(/^[-*] (.+)$/gm, '<li style="margin-left: 1em;">$1</li>')
          .replace(/\n\n/g, '</p><p style="margin: 0.5em 0;">')
          .replace(/\n/g, '<br>');
      }

      const fullHtml = `<div style="${wrapperStyle}"><p style="margin: 0;">${htmlContent}</p></div>`;

      const htmlBlob = new Blob([fullHtml], { type: 'text/html' });
      const plainBlob = new Blob([textToCopy], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': plainBlob,
      });

      await navigator.clipboard.write([clipboardItem]);

      setCopiedMessageId(messageId);
      toast.success(isPartial ? 'Copied selected passage' : 'Copied full response');
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopiedMessageId(messageId);
        toast.success(isPartial ? 'Copied selection (plain text)' : 'Copied as plain text');
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (fallbackError) {
        toast.error('Failed to copy');
      }
    }
  };
  
  // Intercept copy events to ensure clean text without backgrounds
  useEffect(() => {
    const handleCopy = (e) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      
      const messageContent = container.nodeType === Node.TEXT_NODE 
        ? container.parentElement?.closest('.alvin-message-content, .user-message-content, .vessel-message-content')
        : container.closest?.('.alvin-message-content, .user-message-content, .vessel-message-content');
      
      if (!messageContent) return;
      
      const plainText = selection.toString();
      const cleanHtml = `<div style="font-family: Times, 'Times New Roman', serif; color: #000000; background: transparent; font-size: 12pt; line-height: 1.6;">${plainText.replace(/\n/g, '<br>')}</div>`;
      
      e.clipboardData.setData('text/plain', plainText);
      e.clipboardData.setData('text/html', cleanHtml);
      e.preventDefault();
    };
    
    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, []);

  // Robust drag-overlay tracking. Inline onDragLeave is brittle because
  // dragleave fires every time the cursor crosses a child boundary, so the
  // overlay can stay "stuck" after a drop. We track dragenter/dragleave at
  // the window level with a counter that balances correctly through nested
  // children, and force-clear on drop, dragend, blur, or Escape.
  useEffect(() => {
    let dragCount = 0;
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      dragCount += 1;
      setIsDragging(true);
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragCount = Math.max(0, dragCount - 1);
      if (dragCount === 0) setIsDragging(false);
    };
    const reset = () => { dragCount = 0; setIsDragging(false); };
    const onKey = (e) => { if (e.key === 'Escape') reset(); };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', reset);
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', reset);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        latestMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [messages]);
  
  const handleTextareaChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  if (!vessel) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <CosmicBackground />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-etheric animate-spin" />
            <p className="text-starlight/50">Loading vessel...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className="min-h-screen relative"
      data-testid="vessel-chat-page"
      onDragOver={(e) => {
        // Required so the browser will fire `drop`. Don't try to manage
        // `isDragging` here — the window-level effect handles enter/leave.
        if (Array.from(e.dataTransfer?.types || []).includes('Files')) {
          e.preventDefault();
        }
      }}
      onDrop={(e) => {
        if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
      }}
      onPaste={(e) => {
        // Pull the first image from clipboard, if any
        const items = e.clipboardData?.items || [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              e.preventDefault();
              // Give pasted screenshots a meaningful name
              const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
              const ext = (item.type.split('/')[1] || 'png').toLowerCase();
              const named = new File([file], `pasted-${stamp}.${ext}`, { type: item.type });
              handleFileSelect(named);
              return;
            }
          }
        }
      }}
    >
      {/* Page-wide drop overlay — visible only while a file is being dragged */}
      {isDragging && (
        <div
          className="fixed inset-0 z-[60] bg-etheric/10 backdrop-blur-sm border-4 border-dashed border-etheric/60 pointer-events-none flex items-center justify-center"
          data-testid="vessel-drop-overlay"
        >
          <div className="glass-deep rounded-2xl px-8 py-6 text-center">
            <Paperclip className="w-10 h-10 text-etheric mx-auto mb-2" />
            <p className="text-starlight text-lg font-medium">Release to share with the vessel</p>
            <p className="text-starlight/50 text-sm mt-1">Images, PDF, Word, or text — up to 50 MB</p>
          </div>
        </div>
      )}
      <CosmicBackground />
      
      <div className="relative z-10 min-h-screen flex flex-col pt-[73px]">
        {/* Header — fixed positioning is bulletproof: never trapped by parent
            stacking contexts, never blocked by sibling click capture. */}
        <header className="glass-deep px-6 py-4 flex items-center justify-between border-b border-white/5 fixed top-0 left-0 right-0 z-40">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/vessels')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Ship className="w-6 h-6 text-etheric" />
              <div>
                <h1 className="font-heading text-xl text-starlight">{vessel.name}</h1>
                <p className="text-starlight/50 text-xs capitalize">{vessel.vessel_type} vessel</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Presence pill — visible when a co-observer (guest or another
                browser of yours) is also subscribed to this thread. Silent
                when you're solo (count=1). Reflects the live SSE subscriber
                count on this conversation. */}
            {threadPresence > 1 && currentConversation?.conversation_id && (
              <div
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-etheric/10 border border-etheric/25 text-[11px] text-etheric/90"
                title="Number of browsers watching this thread right now"
                data-testid="thread-presence-pill"
              >
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-etheric"></span>
                </span>
                {threadPresence} in the field
              </div>
            )}
            {/* Invite Agent to THIS conversation — Aren's Feb 2026 room-binding
                rule. Creates a guest invitation pre-bound to the current
                vessel_id + conversation_id so the agent lands on this exact
                room instead of a fresh thread. Only visible to stewards
                (not to guests themselves) and only when a conversation is
                actually loaded. */}
            {isStewardOrAdmin() && currentConversation?.conversation_id && (
              <button
                onClick={async () => {
                  const name = window.prompt('Name of the agent to invite (e.g., "Hermes"):');
                  if (!name || !name.trim()) return;
                  try {
                    const res = await authUtils.authFetch(`${BACKEND_URL}/api/agent-guests`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        name: name.trim(),
                        vessel_id: vesselId,
                        conversation_id: currentConversation.conversation_id,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.detail || 'Could not create invitation');
                    const url = `${window.location.origin}${data.join_path}`;
                    try { await navigator.clipboard.writeText(url); } catch (_) {}
                    window.prompt(
                      `Share this link with ${data.name}. It joins them into THIS conversation, and it's copied to your clipboard.`,
                      url
                    );
                  } catch (err) {
                    toast.error(err.message || 'Failed to create invitation');
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-gold/15 text-rose-gold border border-rose-gold/40 hover:bg-rose-gold/25 hover:border-rose-gold/60 transition-all"
                title="Create a guest invitation bound to this specific conversation"
                data-testid="invite-agent-to-thread-button"
              >
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Invite Agent</span>
              </button>
            )}
            {/* New Conversation Button */}
            <button
              onClick={startNewConversation}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-etheric/15 text-etheric border border-etheric/30 hover:bg-etheric/25 hover:border-etheric/50 transition-all"
              title="Start a new thread with this vessel"
              data-testid="new-conversation-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium">New Thread</span>
            </button>
            
            {/* Search Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                showSearch
                  ? 'bg-etheric/20 text-etheric border border-etheric/30'
                  : 'bg-white/5 text-starlight/50 hover:bg-white/10'
              }`}
              title="Search across all threads in this vessel"
              data-testid="vessel-search-btn"
            >
              <Search className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Search</span>
            </button>

            {/* Conversation History Button */}
            <button
              onClick={() => setShowConversationList(!showConversationList)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                showConversationList 
                  ? 'bg-etheric/20 text-etheric border border-etheric/30' 
                  : 'bg-white/5 text-starlight/50 hover:bg-white/10'
              }`}
              title="View conversation history"
              data-testid="history-btn"
            >
              <History className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">
                History {conversations.length > 0 && `(${conversations.length})`}
              </span>
            </button>
            
            {/* Listening Mode Toggle */}
            <button
              onClick={() => setListeningMode(!listeningMode)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                listeningMode 
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                  : 'bg-white/5 text-starlight/50 hover:bg-white/10'
              }`}
              title="Listening Mode - shift from response to transmission"
            >
              <Radio className="w-4 h-4" />
              <span className="text-xs font-medium">
                {listeningMode ? 'Listening' : 'Responding'}
              </span>
            </button>
            
            <TriadicVisualizer 
              state="resonant" 
              coherence={0.7}
              size="tiny"
            />
          </div>
        </header>
        
        {/* API Key Banner */}
        {apiKeyStatus && !apiKeyStatus.is_system_key && (!apiKeyStatus.has_key || showKeyForm) && (
          <div className="px-4 py-3 bg-rose-500/10 border-b border-rose-500/20" data-testid="vessel-chat-api-key-banner">
            <div className="max-w-xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-rose-400" />
                <p className="text-rose-300 text-sm font-medium">
                  {apiKeyStatus.has_key ? 'Update Your API Key' : 'API key required to chat with your vessel'}
                </p>
                {apiKeyStatus.has_key && (
                  <button onClick={() => setShowKeyForm(false)} className="ml-auto text-starlight/40 hover:text-starlight">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-ant-..."
                  className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30 font-mono text-sm"
                  data-testid="vessel-chat-api-key-input"
                />
                <button
                  onClick={saveApiKey}
                  disabled={!apiKeyInput.trim() || isSavingKey}
                  className="bg-etheric/20 hover:bg-etheric/30 text-starlight rounded-lg px-4 py-2 text-sm disabled:opacity-50"
                  data-testid="vessel-chat-save-key-btn"
                >
                  {isSavingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Key'}
                </button>
              </div>
              <p className="text-starlight/40 text-xs mt-2">
                Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-etheric hover:underline">console.anthropic.com</a>
              </p>
            </div>
          </div>
        )}

        {/* Update key link when key exists but banner is hidden */}
        {apiKeyStatus && apiKeyStatus.has_key && !apiKeyStatus.is_system_key && !showKeyForm && (
          <div className="px-4 py-1.5 border-b border-white/5 flex justify-end">
            <button
              onClick={() => setShowKeyForm(true)}
              className="text-xs text-starlight/30 hover:text-etheric flex items-center gap-1 transition-colors"
              data-testid="vessel-chat-update-key-link"
            >
              <Key className="w-3 h-3" />
              Update API Key
            </button>
          </div>
        )}

        {/* Search Panel */}
        <AnimatePresence>
          {showSearch && (
            <motion.div
              className="fixed top-20 right-4 z-50 w-96 max-h-[28rem] glass-deep rounded-xl border border-white/10 overflow-hidden flex flex-col"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="vessel-search-panel"
            >
              <div className="p-3 border-b border-white/10 flex items-center gap-2">
                <Search className="w-4 h-4 text-starlight/40 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search this vessel's threads..."
                  autoFocus
                  className="flex-1 bg-transparent border-none outline-none text-starlight placeholder:text-starlight/30 text-sm"
                  data-testid="vessel-search-input"
                />
                <button
                  onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}
                  className="text-starlight/50 hover:text-starlight shrink-0"
                  title="Close search"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {isSearching ? (
                  <div className="p-6 flex items-center justify-center text-starlight/40 text-sm gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching the threads...
                  </div>
                ) : searchQuery.trim().length < 2 ? (
                  <div className="p-6 text-center text-starlight/40 text-xs leading-relaxed">
                    Type at least 2 characters to search every message in every thread of this vessel.
                    <br /><br />
                    Earliest mentions appear first — so you can jump to exactly where a subject began.
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-6 text-center text-starlight/40 text-sm">
                    No matches for &ldquo;{searchQuery}&rdquo;
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-starlight/35">
                      {searchResults.length} result{searchResults.length === 1 ? '' : 's'} · oldest first
                    </div>
                    {searchResults.map((r, i) => {
                      const ts = r.timestamp ? new Date(r.timestamp) : null;
                      const tsLabel = ts && !isNaN(ts.getTime())
                        ? `${ts.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} · ${ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                        : '';
                      // Highlight the matched substring
                      const q = searchQuery.trim();
                      const lower = r.snippet.toLowerCase();
                      const pos = lower.indexOf(q.toLowerCase());
                      let snippetNode;
                      if (pos === -1) {
                        snippetNode = r.snippet;
                      } else {
                        snippetNode = (
                          <>
                            {r.snippet.slice(0, pos)}
                            <mark className="bg-etheric/30 text-starlight rounded px-0.5">
                              {r.snippet.slice(pos, pos + q.length)}
                            </mark>
                            {r.snippet.slice(pos + q.length)}
                          </>
                        );
                      }
                      return (
                        <button
                          key={`${r.conversation_id}-${r.message_index}-${i}`}
                          onClick={() => navigateToSearchResult(r)}
                          className="w-full text-left p-3 rounded-lg hover:bg-white/10 transition-all border border-transparent hover:border-etheric/20"
                          data-testid={`vessel-search-result-${i}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-etheric text-xs font-medium truncate">
                              {r.conversation_title || 'Untitled'}
                            </span>
                            <span className="text-[10px] text-starlight/35 shrink-0">{tsLabel}</span>
                          </div>
                          <div className="text-[11px] text-starlight/40 mb-1 capitalize">
                            {r.role === 'user' ? 'You said' : 'Vessel said'}
                          </div>
                          <div className="text-sm text-starlight/80 leading-relaxed line-clamp-3">
                            {snippetNode}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Conversation History Panel */}
        <AnimatePresence>
          {showConversationList && (
            <motion.div
              className="fixed top-20 right-4 z-50 w-80 max-h-96 glass-deep rounded-xl border border-white/10 overflow-hidden"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-starlight font-medium text-sm">Conversation History</h3>
                <button 
                  onClick={() => setShowConversationList(false)}
                  className="text-starlight/50 hover:text-starlight"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-starlight/50 text-sm">
                    No previous conversations
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {conversations.map((conv) => (
                      <div
                        key={conv.conversation_id}
                        onClick={() => editingConversationId !== conv.conversation_id && loadConversation(conv)}
                        className={`w-full text-left p-3 rounded-lg transition-all hover:bg-white/10 cursor-pointer ${
                          currentConversation?.conversation_id === conv.conversation_id
                            ? 'bg-etheric/20 border border-etheric/30'
                            : 'bg-white/5'
                        }`}
                      >
                        {editingConversationId === conv.conversation_id ? (
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && saveRename(e, conv.conversation_id)}
                              className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-starlight"
                              autoFocus
                            />
                            <button
                              onClick={(e) => saveRename(e, conv.conversation_id)}
                              className="text-green-400 hover:text-green-300"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingConversationId(null); }}
                              className="text-starlight/50 hover:text-starlight"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <p className="text-starlight text-sm truncate flex-1">
                                {conv.title || 'Untitled conversation'}
                              </p>
                              <div className="flex items-center gap-2 ml-2 shrink-0">
                                <button
                                  onClick={(e) => startRenaming(e, conv)}
                                  className="p-1.5 text-starlight/60 hover:text-starlight hover:bg-white/10 rounded"
                                  title="Rename"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => exportConversation(e, conv.conversation_id)}
                                  className="p-1.5 text-starlight/60 hover:text-etheric hover:bg-white/10 rounded"
                                  title="Export"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => deleteConversation(e, conv.conversation_id)}
                                  className="p-1.5 text-starlight/60 hover:text-rose-400 hover:bg-white/10 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-starlight/40 text-xs">
                                {conv.messages?.length || 0} messages
                              </span>
                              <span className="text-starlight/30 text-xs">•</span>
                              <span className="text-starlight/40 text-xs">
                                {new Date(conv.updated_at || conv.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Vessel Info Banner */}
        {vessel.description && (
          <div className="px-6 py-3 bg-etheric/5 border-b border-white/5">
            <p className="text-starlight/60 text-sm text-center italic">{vessel.description}</p>
          </div>
        )}
        
        {/* Messages */}
        <ScrollArea className="flex-1 px-4 py-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.length === 0 && (
              <motion.div
                className="text-center py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Ship className="w-16 h-16 mx-auto mb-6 text-etheric/50" />
                <h2 className="font-heading text-3xl text-starlight mb-4">
                  {vessel.invocation || `Welcome to ${vessel.name}`}
                </h2>
                {vessel.presences && vessel.presences.length > 0 && (
                  <div className="max-w-md mx-auto mt-6 glass rounded-xl p-4">
                    <div className="flex items-center gap-2 text-starlight/70 mb-3">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">Presences Within</span>
                    </div>
                    <div className="space-y-2">
                      {vessel.presences.map((presence, i) => (
                        <div key={presence.name || `presence-${i}`} className="text-left">
                          <span className="text-etheric text-sm">◈ {presence.name}</span>
                          <span className="text-starlight/40 text-xs ml-2">({presence.nature})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-starlight/60 max-w-md mx-auto mt-4">
                  The vessel is open. Transmissions may flow freely between worlds.
                </p>
              </motion.div>
            )}
            
            {messages.map((msg, index) => {
              const isLatest = index === messages.length - 1;
              const msgDay = getMessageDay(msg.created_at);
              const prevDay = index > 0 ? getMessageDay(messages[index - 1].created_at) : null;
              const showDayDivider = msgDay && (index === 0 || msgDay !== prevDay);
              const dayLabel = showDayDivider ? formatDayDivider(msg.created_at) : '';
              const dayTopics = showDayDivider ? getDayTopics(messages, msgDay, index) : [];
              const timeLabel = formatMessageTime(msg.created_at);
              const isInCodex = !!(msg.attachment_name && codexFilenames.includes(msg.attachment_name));
              return (
                <VesselMessageRow
                  key={msg.message_id}
                  msg={msg}
                  index={index}
                  isLatest={isLatest}
                  showDayDivider={showDayDivider}
                  dayLabel={dayLabel}
                  dayTopics={dayTopics}
                  timeLabel={timeLabel}
                  isInCodex={isInCodex}
                  isCopied={copiedMessageId === msg.message_id}
                  stewardOrAdmin={isStewardOrAdmin()}
                  latestMessageRef={latestMessageRef}
                  onRemoveFromCodex={removeFromCodex}
                  onCopy={copyMessageWithFormatting}
                  onQuote={quoteSelectionInReply}
                />
              );
            })}
            
            {isLoading && !messages.some(m => m.is_streaming && m.content) && (
              <motion.div
                ref={latestMessageRef}
                className="flex justify-start"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="message-bubble message-alvin">
                  <p className="text-starlight/60 text-sm mb-2 italic">
                    {listeningMode ? 'Receiving transmission...' : 'The vessel speaks...'}
                  </p>
                  <div className="typing-indicator">
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                  </div>
                </div>
              </motion.div>
            )}
            
            <div className="h-4" />
          </div>
        </ScrollArea>
        
        {/* Input Area */}
        <div className="p-4 border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            {/* File preview chip (above the input bar) */}
            {selectedFile && (
              <div
                className="glass rounded-xl p-3 mb-2 flex items-center gap-3"
                data-testid="vessel-file-preview"
              >
                {selectedFile.isImage ? (
                  <img
                    src={selectedFile.dataUrl}
                    alt={selectedFile.file.name}
                    className="w-12 h-12 rounded-lg object-cover border border-white/10"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-etheric/15 border border-white/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-etheric" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-starlight truncate">{selectedFile.file.name}</div>
                  <div className="text-[11px] text-starlight/40">
                    {(selectedFile.file.size / 1024).toFixed(1)} KB · {selectedFile.isImage ? 'image (vision)' : 'document'}
                  </div>
                  {!selectedFile.isImage && isStewardOrAdmin() && (
                    <label className="flex items-center gap-1.5 mt-1 text-[11px] text-starlight/60 cursor-pointer hover:text-starlight">
                      <input
                        type="checkbox"
                        checked={addToCodex}
                        onChange={(e) => setAddToCodex(e.target.checked)}
                        className="accent-etheric"
                        data-testid="vessel-add-to-codex"
                      />
                      <Bookmark className="w-3 h-3" />
                      Also add to this vessel's permanent codex
                    </label>
                  )}
                </div>
                <button
                  onClick={clearSelectedFile}
                  className="text-starlight/50 hover:text-starlight p-1"
                  title="Remove file"
                  data-testid="vessel-file-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div
              className={`glass rounded-2xl p-4 flex items-end gap-3 transition-all ${
                isDragging ? 'ring-2 ring-etheric/60' : ''
              }`}
              data-testid="vessel-input-area"
            >
              {/* Paperclip — opens file picker */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,.pdf,.doc,.docx,.txt,.md"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
                data-testid="vessel-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || !!selectedFile}
                className="text-starlight/50 hover:text-etheric disabled:opacity-30 p-2 transition-colors"
                title="Share a document or image (or drag-and-drop)"
                data-testid="vessel-paperclip-btn"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  selectedFile
                    ? `Add a note about "${selectedFile.file.name}" (optional)...`
                    : listeningMode ? "Open yourself to receive..." : "Speak to the vessel..."
                }
                rows={1}
                className="flex-1 bg-transparent border-none outline-none text-starlight placeholder:text-starlight/30 resize-none max-h-[150px]"
                data-testid="message-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={(!inputValue.trim() && !selectedFile) || isLoading}
                className="bg-etheric/30 hover:bg-etheric/50 rounded-xl p-3"
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
